import { Building2, Check, ChevronsUpDown, Layers, Plus } from 'lucide-react';
import { DropdownMenu as RDM } from 'radix-ui';
import type { CSSProperties } from 'react';
import { cx } from '../lib/cx.js';
import { Kbd } from '../primitives/Kbd.js';
import '../primitives/overlay.css';
import './shell.css';

/**
 * Structurally identical to `LocationScope` in @velora/contracts (ADR-0012),
 * so values pass straight through without this package depending on Zod.
 * `all` means every office the user may access, never every office in the practice.
 */
export type OfficeScope = { kind: 'all' } | { kind: 'location'; locationId: string };

export interface OfficeInfo {
  id: string;
  name: string;
  /** Short label for tight chrome, e.g. "MID". */
  shortName?: string;
  /** Muted second line, e.g. city or PMS type. */
  detail?: string;
  /**
   * Stable 0-based colour slot for this office (maps to --chart-N). Keep it
   * fixed per office so charts, dots and the switcher agree everywhere.
   */
  colorIndex?: number;
}

/** `var(--chart-N)` for an office, by its colour slot or list position. */
export function officeColor(office: OfficeInfo, fallbackIndex: number): string {
  const i = office.colorIndex ?? fallbackIndex;
  return `var(--chart-${(i % 8) + 1})`;
}

export interface OfficeSwitcherProps {
  /** Offices the user may access (already filtered by user_location_access). */
  offices: readonly OfficeInfo[];
  value: OfficeScope;
  onChange: (scope: OfficeScope) => void;
  /**
   * False on routes whose officeScope is `location` (e.g. the schedule grid):
   * the "All offices" entry is shown disabled with the reason.
   */
  allowAll?: boolean;
  /** Shown when the user may add offices to the practice. */
  onAddOffice?: () => void;
  /** Keyboard hint shown in the menu header (the shell binds the key). */
  shortcut?: readonly string[];
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Collapsed: dot + short name only. */
  compact?: boolean;
  className?: string;
}

function Dot({ color }: { color: string }) {
  return (
    <span
      className="vel-office__dot"
      style={{ '--office-color': color } as CSSProperties}
      aria-hidden
    />
  );
}

/**
 * Two-letter monogram for an office from its short name (else its name):
 * "Saginaw" is SA, "Harbor Point" is HP, "MID" is MI.
 */
export function officeInitials(office: Pick<OfficeInfo, 'name' | 'shortName'>): string {
  const source = (office.shortName ?? office.name).trim();
  const words = source.split(/[\s-]+/).filter((w) => /[A-Za-z0-9]/.test(w));
  if (words.length >= 2) {
    return ((words[0]?.[0] ?? '') + (words[1]?.[0] ?? '')).toUpperCase();
  }
  return (
    source
      .replace(/[^A-Za-z0-9]/g, '')
      .slice(0, 2)
      .toUpperCase() || '?'
  );
}

/**
 * The office's colour tile with its monogram: the same office looks the
 * same in the selector, the office step, cards and tables.
 */
export function OfficeBadge({
  office,
  index = 0,
  size = 'md',
  className,
}: {
  office: OfficeInfo;
  index?: number;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}) {
  return (
    <span
      className={cx('vel-office-badge', className)}
      data-size={size}
      style={{ '--office-color': officeColor(office, index) } as CSSProperties}
      aria-hidden
    >
      {officeInitials(office)}
    </span>
  );
}

/**
 * Top-bar office selector: one office or "All offices". A person with one
 * office still gets the menu, with "All my offices (1)" (AH-20), so the
 * choice reads the same for everyone; with no office it is a static label.
 */
export function OfficeSwitcher({
  offices,
  value,
  onChange,
  allowAll = true,
  onAddOffice,
  shortcut = ['L'],
  open,
  onOpenChange,
  compact = false,
  className,
}: OfficeSwitcherProps) {
  const selectedIndex =
    value.kind === 'location' ? offices.findIndex((o) => o.id === value.locationId) : -1;
  const selected = selectedIndex >= 0 ? offices[selectedIndex] : undefined;
  const isAll = value.kind === 'all';
  const unknown = value.kind === 'location' && !selected;

  const single = offices.length === 1;
  const allLabel = single ? 'All my offices (1)' : 'All offices';
  const label = isAll
    ? allLabel
    : selected
      ? compact
        ? (selected.shortName ?? selected.name)
        : selected.name
      : 'No office selected';

  const triggerContent = (
    <>
      {isAll ? (
        <span className="vel-office__stack" aria-hidden>
          {offices.slice(0, 4).map((o, i) => (
            <Dot key={o.id} color={officeColor(o, i)} />
          ))}
        </span>
      ) : selected ? (
        <OfficeBadge office={selected} index={selectedIndex} size="sm" />
      ) : (
        <Building2 size={14} aria-hidden />
      )}
      <span className="vel-office__name">{label}</span>
      {isAll && !compact && !single ? (
        <span className="vel-office__count vel-num">{offices.length}</span>
      ) : null}
    </>
  );

  if (offices.length === 0 && !onAddOffice) {
    return (
      <div className={cx('vel-office', className)} data-static="" aria-label={`Office: ${label}`}>
        {triggerContent}
      </div>
    );
  }

  const radioValue = isAll ? '__all' : (selected?.id ?? '');

  return (
    <RDM.Root {...(open !== undefined ? { open } : {})} {...(onOpenChange ? { onOpenChange } : {})}>
      <RDM.Trigger
        className={cx('vel-office', className)}
        data-invalid={unknown ? '' : undefined}
        aria-label={`Office: ${label}. Change office`}
      >
        {triggerContent}
        <ChevronsUpDown size={13} className="vel-office__chev" aria-hidden />
      </RDM.Trigger>
      <RDM.Portal>
        <RDM.Content className="vel-float vel-office__menu" align="start" sideOffset={6}>
          <div className="vel-office__menu-head">
            <span>Switch office</span>
            <Kbd keys={shortcut} />
          </div>
          <RDM.RadioGroup
            value={radioValue}
            onValueChange={(v) => {
              if (v === '__all') onChange({ kind: 'all' });
              else onChange({ kind: 'location', locationId: v });
            }}
          >
            {offices.length > 0 ? (
              <RDM.RadioItem
                value="__all"
                textValue={allLabel}
                className="vel-option vel-office__item"
                disabled={!allowAll}
              >
                <span className="vel-office__item-icon vel-office__item-icon--all" aria-hidden>
                  <Layers size={14} />
                </span>
                <span className="vel-office__item-text">
                  <span>{allLabel}</span>
                  <span className="vel-office__item-detail">
                    {!allowAll
                      ? 'This page works on one office at a time'
                      : single
                        ? 'Every office you have access to'
                        : `${offices.length} offices combined, each shown separately`}
                  </span>
                </span>
                <RDM.ItemIndicator className="vel-option__check">
                  <Check size={14} />
                </RDM.ItemIndicator>
              </RDM.RadioItem>
            ) : null}
            {offices.length > 0 ? <RDM.Separator className="vel-option-sep" /> : null}
            {offices.map((o, i) => (
              <RDM.RadioItem
                key={o.id}
                value={o.id}
                // Type-ahead by the office's own name ("mid" finds Midland even
                // when every full name starts with the practice name, AM-03).
                textValue={o.shortName ?? o.name}
                className="vel-option vel-office__item"
              >
                <OfficeBadge office={o} index={i} className="vel-office__item-icon" />
                <span className="vel-office__item-text">
                  <span>{o.name}</span>
                  {o.detail ? <span className="vel-office__item-detail">{o.detail}</span> : null}
                </span>
                <RDM.ItemIndicator className="vel-option__check">
                  <Check size={14} />
                </RDM.ItemIndicator>
              </RDM.RadioItem>
            ))}
          </RDM.RadioGroup>
          {onAddOffice ? (
            <>
              <RDM.Separator className="vel-option-sep" />
              <RDM.Item className="vel-option" onSelect={onAddOffice}>
                <span className="vel-option__icon">
                  <Plus size={14} aria-hidden />
                </span>
                Add an office
              </RDM.Item>
            </>
          ) : null}
        </RDM.Content>
      </RDM.Portal>
    </RDM.Root>
  );
}
